import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { serviceService } from "../services/serviceService";
import {
  EyeIcon,
  XCircleIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../../../components/common/Pagination";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import DeleteConfirmation from "../../../components/common/DeleteConfirmation";
import { ClockIcon } from "lucide-react";

// Logo avec repli automatique sur l'icône par défaut si l'image casse
// (fichier supprimé du disque, chemin invalide...) plutôt que de laisser
// le navigateur afficher une icône d'image cassée.
const ServiceLogo = ({ service }) => {
  const [hasError, setHasError] = useState(false);

  if (!service.logo_url || hasError) {
    return (
      <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
        <BuildingOfficeIcon className="h-6 w-6 text-white" />
      </div>
    );
  }

  return (
    <img
      src={service.logo_url}
      alt={service.title}
      className="h-10 w-10 rounded-full object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const ServiceList = () => {
  const [page, setPage] = useState(1);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["services", page],
    queryFn: () => serviceService.getAdminServices({ page }),
  });

  const toggleStatusMutation = useMutation({
    mutationFn: (id) => serviceService.toggleServiceStatus(id),
    onSuccess: () => {
      refetch();
      setShowStatusModal(false);
    },
  });

  // Fonction pour parser la location de manière sécurisée
  const safeParseLocation = (location) => {
    if (typeof location === "object" && location !== null) {
      return location;
    }
    if (typeof location === "string") {
      try {
        // Tenter de parser en JSON
        return JSON.parse(location);
      } catch {
        try {
          // Si le JSON est invalide, essayer de parser le format objet JavaScript
          // Remplacer les accolades et parser manuellement
          const cleaned = location.replace(/^\{|\}$/g, "");
          const pairs = cleaned.split(",").map((pair) => pair.trim());
          const result = {};

          pairs.forEach((pair) => {
            const [key, value] = pair.split(":").map((item) => item.trim());
            if (key && value) {
              // Enlever les guillemets si présents et gérer null
              const cleanKey = key.replace(/['"]/g, "");
              let cleanValue = value.replace(/['"]/g, "");
              if (cleanValue === "null") {
                cleanValue = null;
              }
              result[cleanKey] = cleanValue;
            }
          });

          return result;
        } catch (fallbackError) {
          console.warn(
            "Impossible de parser la location:",
            location,
            fallbackError
          );
          return {};
        }
      }
    }
    return {};
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError)
    return (
      <div className="text-red-500">Erreur lors du chargement des services</div>
    );

  const services = (data?.data || []).map((service) => {
    return {
      ...service,
      location: safeParseLocation(service.location),
    };
  });

  const pagination = data?.meta || {};

  const handleViewDetails = (id) => {
    navigate(`/services/${id}`);
  };

  const handleToggleStatus = (service) => {
    setSelectedService(service);
    setShowStatusModal(true);
  };

  const handleStatusConfirm = () => {
    if (selectedService) {
      toggleStatusMutation.mutate(selectedService.id);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-lg">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Les services</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase w-16">
                Logo
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Titre
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Catégorie
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Propriétaire
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Localisation
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Contact
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase">
                Statut
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {services.map((service) => (
              <tr key={service.id}>
                <td className="px-4 py-3">
                  <ServiceLogo service={service} />
                </td>
                <td className="px-4 py-3 text-sm font-semibold">
                  {service.title}
                </td>
                <td className="px-4 py-3 text-sm font-mono text-gray-600">
                  {service.category}
                </td>
                <td className="px-4 py-3 text-sm">
                  {service.owner_firstname} {service.owner_lastname}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="text-xs">
                    <div className="font-medium">{service.city}</div>
                    <div
                      className="text-gray-500 truncate max-w-32"
                      title={service.address}
                    >
                      {service.address}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="text-xs">
                    <div>
                      {service.user?.firstname} {service.user?.lastname}
                    </div>
                    <div className="text-gray-500">
                      {service.user?.phone_number}
                    </div>
                    <div className="text-gray-500">{service.user?.email}</div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      service.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : service.status === "approved"
                        ? "bg-green-100 text-green-800"
                        : service.status === "rejected"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {service.status === "pending"
                      ? "En attente"
                      : service.status === "approved"
                      ? "Approuvé"
                      : service.status === "rejected"
                      ? "Rejeté"
                      : "Inactif"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      title="Voir les détails"
                      className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => handleViewDetails(service.id)}
                    >
                      <EyeIcon className="h-5 w-5 text-blue-600" />
                    </button>
                    <button
                      title={
                        service.status === "pending"
                          ? "Traiter la demande"
                          : service.status === "approved"
                          ? "Désactiver le service"
                          : service.status === "rejected"
                          ? "Approuver le service"
                          : "Réactiver le service"
                      }
                      className={`p-2 rounded cursor-pointer ${
                        service.status === "approved"
                          ? "hover:bg-red-100"
                          : "hover:bg-green-100"
                      }`}
                      onClick={() => handleToggleStatus(service)}
                    >
                      {service.status === "approved" ? (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      ) : service.status === "pending" ? (
                        <ClockIcon className="h-5 w-5 text-yellow-500" />
                      ) : service.status === "rejected" ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        totalItems={pagination.total}
        itemsPerPage={pagination.per_page}
        onPageChange={setPage}
      />

      {/* Modal de confirmation */}
      <DeleteConfirmation
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onConfirm={handleStatusConfirm}
        title={
          selectedService?.status === "pending"
            ? "Traiter la demande"
            : selectedService?.status === "approved"
            ? "Désactiver le service"
            : selectedService?.status === "rejected"
            ? "Approuver le service"
            : "Réactiver le service"
        }
        message={
          selectedService?.status === "pending"
            ? `Êtes-vous sûr de vouloir traiter la demande pour ${selectedService?.title} ?`
            : selectedService?.status === "approved"
            ? `Êtes-vous sûr de vouloir désactiver le service ${selectedService?.title} ?`
            : selectedService?.status === "rejected"
            ? `Êtes-vous sûr de vouloir approuver le service ${selectedService?.title} ?`
            : `Êtes-vous sûr de vouloir réactiver le service ${selectedService?.title} ?`
        }
        confirmButtonText={
          selectedService?.status === "pending"
            ? "Approuver"
            : selectedService?.status === "approved"
            ? "Désactiver"
            : selectedService?.status === "rejected"
            ? "Approuver"
            : "Réactiver"
        }
        confirmButtonColor={
          selectedService?.status === "pending"
            ? "yellow"
            : selectedService?.status === "approved"
            ? "red"
            : selectedService?.status === "rejected"
            ? "green"
            : "green"
        }
      />
    </div>
  );
};

export default ServiceList;
